/**
 * Avatars (03 §3.1, AC-PRO-04, AC-ROLE-05): the uploaded picture; else the initials of the display name's first two
 * words on a colour picked by a hash of the user id (12 colours, white text ≥ 4.5:1); bots show a robot.
 */

/** 12 colours, each with white text at a contrast of at least 4.5:1. */
const AVATAR_PALETTE = [
  '#1f5f99',
  '#7a3e9d',
  '#a23b3b',
  '#2e7d32',
  '#00695c',
  '#ad4a00',
  '#5d4037',
  '#37474f',
  '#c2185b',
  '#283593',
  '#33691e',
  '#0277bd',
] as const;

/** The palette colour of a user id (FNV-1a). */
function avatarColour(id: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h = Math.imul(h ^ id.charCodeAt(i), 0x01000193);
  }
  return AVATAR_PALETTE[(h >>> 0) % AVATAR_PALETTE.length] ?? AVATAR_PALETTE[0];
}

/** The initials of the first two words, upper case. */
function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/[\s._-]+/u)
    .filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => [...w][0] ?? '')
    .join('')
    .toLocaleUpperCase();
}

/** An avatar of `size` px. */
export function Avatar({
  url,
  name,
  id,
  bot = false,
  size = 32,
}: {
  url?: string | null;
  name: string;
  id: string;
  bot?: boolean;
  size?: number;
}) {
  if (bot) {
    return (
      <svg
        class="avatar robot"
        width={size}
        height={size}
        viewBox="0 0 32 32"
        role="img"
        aria-label={name}
        data-avatar="robot"
      >
        <rect width="32" height="32" rx="16" fill="#37474f" />
        <rect x="8" y="10" width="16" height="13" rx="3" fill="#cfd8dc" />
        <circle cx="13" cy="16" r="2" fill="#263238" />
        <circle cx="19" cy="16" r="2" fill="#263238" />
        <rect x="15" y="5" width="2" height="5" fill="#cfd8dc" />
        <circle cx="16" cy="5" r="2" fill="#e0b44c" />
      </svg>
    );
  }
  if (url) {
    return <img class="avatar" src={url} alt={name} width={size} height={size} data-avatar="image" />;
  }
  return (
    <svg
      class="avatar"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      role="img"
      aria-label={name}
      data-avatar="initials"
    >
      <circle cx="16" cy="16" r="16" fill={avatarColour(id)} />
      <text x="16" y="21" text-anchor="middle" font-size="13" font-weight="600" fill="#fff">
        {initialsOf(name)}
      </text>
    </svg>
  );
}
